import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const STORAGE = "sp_cart_v1";

function load() {
    try {
        const raw = localStorage.getItem(STORAGE);
        if (raw) return JSON.parse(raw);
    } catch {}
    return { items: [], boxes: [], giftWrap: false, giftMessage: "" };
}

export function CartProvider({ children }) {
    const [cart, setCart] = useState(load);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem(STORAGE, JSON.stringify(cart));
    }, [cart]);

    const addItem = (line) => {
        setCart((c) => {
            const key = (l) => `${l.product_id}-${l.size_key}-${l.customization ? `${l.customization.base}|${l.customization.topping}|${l.customization.drizzle}` : ""}`;
            const existing = c.items.find((i) => key(i) === key(line));
            let items;
            if (existing) {
                items = c.items.map((i) => (key(i) === key(line) ? { ...i, quantity: i.quantity + line.quantity } : i));
            } else {
                items = [...c.items, line];
            }
            return { ...c, items };
        });
        setOpen(true);
    };

    const addBox = (box) => {
        setCart((c) => ({ ...c, boxes: [...c.boxes, box] }));
        setOpen(true);
    };

    const updateItemQty = (idx, qty) => {
        setCart((c) => {
            const items = [...c.items];
            if (qty <= 0) items.splice(idx, 1);
            else items[idx] = { ...items[idx], quantity: qty };
            return { ...c, items };
        });
    };

    const updateBoxQty = (idx, qty) => {
        setCart((c) => {
            const boxes = [...c.boxes];
            if (qty <= 0) boxes.splice(idx, 1);
            else boxes[idx] = { ...boxes[idx], quantity: qty };
            return { ...c, boxes };
        });
    };

    const removeItem = (idx) => setCart((c) => ({ ...c, items: c.items.filter((_, i) => i !== idx) }));
    const removeBox = (idx) => setCart((c) => ({ ...c, boxes: c.boxes.filter((_, i) => i !== idx) }));
    const setGiftWrap = (v) => setCart((c) => ({ ...c, giftWrap: v }));
    const setGiftMessage = (v) => setCart((c) => ({ ...c, giftMessage: v }));
    const clear = () => setCart({ items: [], boxes: [], giftWrap: false, giftMessage: "" });

    const count = useMemo(
        () =>
            cart.items.reduce((s, i) => s + i.quantity, 0) +
            cart.boxes.reduce((s, b) => s + b.quantity, 0),
        [cart]
    );

    const subtotal = useMemo(() => {
        const items = cart.items.reduce((s, i) => s + i.unit_price * i.quantity, 0);
        const boxes = cart.boxes.reduce((s, b) => s + b.price * b.quantity, 0);
        const wrap = cart.giftWrap ? 6 : 0;
        return items + boxes + wrap;
    }, [cart]);

    return (
        <CartContext.Provider
            value={{
                cart, count, subtotal, open, setOpen,
                addItem, addBox, updateItemQty, updateBoxQty,
                removeItem, removeBox, setGiftWrap, setGiftMessage, clear,
            }}
        >
            {children}
        </CartContext.Provider>
    );
}
